/**
 * WebSocket observability helper.
 *
 * Wraps a y-websocket `WebsocketProvider` (or any compatible event emitter)
 * with three pieces of telemetry:
 *
 *   1. **Sentry breadcrumbs** — every status change, sync event, close code,
 *      and error becomes a breadcrumb in the `ws` category. When something
 *      else throws, the Sentry event then includes the WS history of the
 *      session, which is what the chandler+marketing investigation needed
 *      and didn't have.
 *
 *   2. **`ws.first_useful_message` Sentry span** — starts when the provider
 *      enters 'connecting' and ends when y-websocket fires its first 'sync'
 *      event (signaling "the server has applied my state and sent its
 *      diff back; the local Y.Doc is now up to date"). This is the exact
 *      latency the user perceives — the connection-age timer in DevTools
 *      includes time after sync where the consumer still hasn't rendered.
 *      Distinguishing the two is what lets us answer "is the WS slow, or
 *      is the consumer slow?" — the question the chandler incident left open.
 *
 *   3. **Cumulative-downtime alert + `ws_health_issue` beacon** — Sentry was
 *      completely silent for the chandler+marketing incident itself (no
 *      client-side exception ever fired), so this also emits a
 *      Sentry-independent, CloudWatch-queryable analytics beacon (see
 *      wsHealthBeacon.ts) for a provider that never reaches its first sync
 *      within 30s, or accumulates 2+ minutes of not-synced time while the
 *      tab is foregrounded — see the "Cumulative not-synced tracking"
 *      section below for why this is cumulative-time-based rather than a
 *      single timer armed on 'disconnected'.
 *
 * Usage:
 *   const provider = new WebsocketProvider(url, room, doc, opts);
 *   const obs = attachWsObservability(provider, { room, prefix: 'org' });
 *   // optionally on cleanup: obs.dispose();
 *
 * The helper is intentionally a one-line attachment because Phase 2 will
 * replace all the direct WebsocketProvider construction sites with the
 * WSClient singleton — at which point the singleton's constructor calls this
 * helper once, and we delete the per-callsite invocations.
 *
 * Reference: `/workspace/.cursor/plans/websocket_robustness_refactor_b886ced2.plan.md`
 */

import * as SentryDefault from '@sentry/react';
import { reportNeverSynced, reportCumulativeDowntime } from './wsHealthBeacon';

// Minimal duck-typed provider interface — keeps the helper testable without
// pulling in the entire y-websocket package.
export interface WsProviderLike {
	on(event: string, handler: (...args: any[]) => void): void;
	off(event: string, handler: (...args: any[]) => void): void;
	// y-websocket exposes the underlying WebSocket as `.ws`. May be null
	// while the provider is between reconnect attempts.
	ws?: WebSocket | null;
}

export interface WsObservabilityMetadata {
	/** Full room name (e.g. 'org:69bb4cbf...'). Truncated for breadcrumbs. */
	room: string;
	/** Room kind (e.g. 'org', 'doc', 'notification'). Used as Sentry tag. */
	prefix: string;
	/** Optional auth0 sub for cross-referencing user-specific issues. */
	authId?: string | null;
}

// Minimal Sentry surface we depend on — lets tests inject a stub.
export interface SentryLike {
	addBreadcrumb(crumb: {
		category: string;
		message: string;
		level?: 'debug' | 'info' | 'warning' | 'error';
		data?: Record<string, unknown>;
	}): void;
	startInactiveSpan?(opts: {
		name: string;
		op?: string;
		attributes?: Record<string, unknown>;
	}): { end(): void; setAttribute?(key: string, value: unknown): void } | null | undefined;
	captureMessage?(
		message: string,
		levelOrOpts?:
			| 'debug' | 'info' | 'warning' | 'error'
			| {
				level?: 'debug' | 'info' | 'warning' | 'error';
				extra?: Record<string, unknown>;
				fingerprint?: string[];
				tags?: Record<string, string>;
			},
	): void;
}

export interface WsObservabilityHandle {
	/** Detach all listeners. Idempotent. */
	dispose(): void;
	/**
	 * Force-end the first-useful-message span as 'aborted' (e.g. the room
	 * was destroyed before sync). Tests use this; production normally lets
	 * the span end naturally on first sync.
	 */
	abortFirstUsefulMessageSpan(reason: string): void;
}

const BREADCRUMB_CATEGORY = 'ws';

// How long a provider must stay disconnected, while the tab is foregrounded,
// before we escalate from a breadcrumb (silent context) to a Sentry message
// (actively alerted). Chosen to comfortably clear wsReconnectCoordinator's
// own recovery paths (5s health-check watchdog, staggered resume) — if a
// provider is STILL down after 2 minutes of active foreground time, none of
// the automatic recovery is working and a human should know.
const PROLONGED_DISCONNECT_MS = 2 * 60 * 1000;

// How often to re-check cumulative not-synced time against the threshold
// above. A periodic check (rather than a single setTimeout armed once per
// status transition) is what makes this immune to a retry loop resetting
// the clock — see the module doc's cumulative-downtime section.
const CUMULATIVE_CHECK_INTERVAL_MS = 10_000;

// A provider that never fires its first 'sync' within this long of opening
// is worth a beacon on its own, independent of whether it eventually stays
// down long enough to also trip the cumulative-downtime alert — a slow but
// eventually-successful first sync is a different failure mode (server/
// network latency) than a permanently wedged connection.
const NEVER_SYNCED_BEACON_MS = 30_000;

// How long to wait, after the tab becomes visible again, before evaluating
// the cumulative-downtime threshold — instead of checking synchronously in
// the visibilitychange handler. wsReconnectCoordinator deliberately
// disconnects every provider once a tab has been hidden for 30s
// (HIDDEN_PAUSE_THRESHOLD_MS in wsReconnectCoordinator.ts) to prevent a
// reconnect storm, then reconnects on a settle+stagger schedule
// (VISIBILITY_SETTLE_MS + RECONNECT_STAGGER_MS per provider) once visible
// again. Without this grace period, ANY tab that was simply backgrounded for
// 2+ minutes — an extremely common, entirely benign pattern — trips this
// alert the instant it's foregrounded again, before the coordinator's own
// reconnect has even had a chance to run. This value is a comfortable margin
// over that resume schedule (plus handshake/network latency) so a normal
// wake-and-reconnect never misfires the alert, while a connection that's
// STILL down after this grace period (the actual chandler+marketing failure
// mode — reconnect-on-wake itself broken) still gets caught, just a few
// seconds later than before.
const VISIBLE_RECOVERY_GRACE_MS = 8_000;

function isForegrounded(): boolean {
	// No `document` (SSR/tests/non-browser) — fail open (treat as foregrounded)
	// rather than silently suppressing a real alert.
	if (typeof document === 'undefined') return true;
	return document.visibilityState === 'visible';
}

function shortRoom(room: string): string {
	if (room.length <= 18) return room;
	return `${room.slice(0, 6)}…${room.slice(-10)}`;
}

export function attachWsObservability(
	provider: WsProviderLike,
	metadata: WsObservabilityMetadata,
	sentry: SentryLike = SentryDefault as SentryLike,
): WsObservabilityHandle {
	const roomShort = shortRoom(metadata.room);
	const baseData = {
		room: roomShort,
		prefix: metadata.prefix,
		...(metadata.authId ? { authIdSuffix: metadata.authId.slice(-12) } : {}),
	};

	const breadcrumb = (
		message: string,
		level: 'debug' | 'info' | 'warning' | 'error' = 'info',
		extra: Record<string, unknown> = {},
	) => {
		try {
			sentry.addBreadcrumb({
				category: BREADCRUMB_CATEGORY,
				message,
				level,
				data: { ...baseData, ...extra },
			});
		} catch {
			// Sentry should never throw, but the helper must never block the
			// real WS work even if it does.
		}
	};

	// First-useful-message span. Starts at 'connecting' (the moment the
	// provider attempts to open the socket); ends at 'sync' (the first sync
	// event from the server, meaning the local Y.Doc reflects authoritative
	// state). If the provider is destroyed mid-flight, abortFirstUsefulMessageSpan
	// closes the span with an 'aborted' attribute so percentile analysis can
	// filter them out.
	let firstUsefulSpan: ReturnType<NonNullable<SentryLike['startInactiveSpan']>> | null | undefined = null;
	let firstUsefulSpanStartedAt = 0;
	let firstSyncSeen = false;

	const startFirstUsefulSpan = () => {
		if (firstUsefulSpan || firstSyncSeen) return;
		if (typeof sentry.startInactiveSpan !== 'function') return;
		try {
			firstUsefulSpan = sentry.startInactiveSpan({
				name: 'ws.first_useful_message',
				op: 'ws.connect',
				attributes: { ...baseData },
			});
			firstUsefulSpanStartedAt = Date.now();
		} catch {
			firstUsefulSpan = null;
		}
	};

	const endFirstUsefulSpan = (status: 'sync' | 'aborted', reason?: string) => {
		if (!firstUsefulSpan) return;
		try {
			firstUsefulSpan.setAttribute?.('status', status);
			if (reason) firstUsefulSpan.setAttribute?.('reason', reason);
			if (firstUsefulSpanStartedAt) {
				firstUsefulSpan.setAttribute?.('elapsed_ms', Date.now() - firstUsefulSpanStartedAt);
			}
			firstUsefulSpan.end();
		} catch {
			// noop
		}
		firstUsefulSpan = null;
	};

	// ── Two independent timers, one shared periodic checker ──────────────────
	// Breadcrumbs alone are silent until something ELSE throws and pulls in the
	// session history. A room stuck without real data flow, with nothing else
	// erroring (exactly the chandler+marketing incident — no client-side
	// exception, just a real-time sync gap nobody was watching for), needs its
	// own active signal. Two distinct failure modes, two distinct clocks:
	//
	// Timer A — "never synced within 30s of open": piggybacks on
	// firstUsefulSpanStartedAt (set once, at the FIRST 'connecting', never
	// reset by later status noise) and only clears on the first real 'sync'.
	// A slow-but-eventually-successful first sync (network/server latency) is
	// a different failure signature than a permanently wedged connection, so
	// it gets its own beacon rather than folding into Timer B below.
	//
	// Timer B — "cumulative not-CONNECTED time >= 2min while visible": tracks
	// elapsed time since entering 'disconnected'/'connecting', immune to a
	// stuck retry loop (disconnected -> connecting -> disconnected -> ...)
	// resetting the clock on every 'connecting' transition — that reset-on-
	// any-status-change bug is exactly what let cumulative downtime sail past
	// the threshold while foregrounded without ever alerting. Evaluated on
	// the periodic interval AND (after VISIBLE_RECOVERY_GRACE_MS) on
	// visibility restore, so an outage that started while hidden isn't
	// missed — but never evaluated synchronously on visibilitychange, since
	// a tab backgrounded for 2+ minutes and then reconnecting normally
	// within a couple of seconds (wsReconnectCoordinator's deliberate
	// pause-on-hide) is expected lifecycle, not a bug. Reaching a
	// genuine 'connected' status (or a 'sync') clears it — an actual
	// reconnect ends this specific outage episode even if this particular
	// room's own connected-but-unsynced zombie case is separately covered by
	// wsReconnectCoordinator's faster (25s) zombie-detection sweep.
	let neverSyncedBeaconFired = false;
	let disconnectedSince: number | null = null;
	let alertFiredForEpisode: number | null = null;
	let cumulativeCheckTimer: ReturnType<typeof setInterval> | null = null;
	let visibleRecoveryTimer: ReturnType<typeof setTimeout> | null = null;

	// The most recent WebSocket close code/reason seen via 'connection-close'.
	// Read by Timer B when the cumulative-downtime alert fires so the alert
	// carries the actual cause (server idle timeout 4408, client watchdog
	// 4408/'forced', abnormal drop 1006, deploy-drain close, ...) instead of
	// just a duration — see onConnectionClose below for where this is set.
	let lastCloseCode: number | undefined;
	let lastCloseReason: string | undefined;

	const ensureTimerRunning = () => {
		if (cumulativeCheckTimer === null) {
			cumulativeCheckTimer = setInterval(checkPeriodic, CUMULATIVE_CHECK_INTERVAL_MS);
		}
	};

	const stopTimerIfHealthy = () => {
		if (firstSyncSeen && disconnectedSince === null && cumulativeCheckTimer !== null) {
			clearInterval(cumulativeCheckTimer);
			cumulativeCheckTimer = null;
		}
	};

	function checkPeriodic() {
		// Timer A
		if (!firstSyncSeen && !neverSyncedBeaconFired && firstUsefulSpanStartedAt) {
			const openElapsedMs = Date.now() - firstUsefulSpanStartedAt;
			if (openElapsedMs >= NEVER_SYNCED_BEACON_MS) {
				neverSyncedBeaconFired = true;
				reportNeverSynced(metadata.prefix, openElapsedMs);
			}
		}

		// Timer B
		if (disconnectedSince !== null && isForegrounded() && alertFiredForEpisode !== disconnectedSince) {
			const downMs = Date.now() - disconnectedSince;
			if (downMs >= PROLONGED_DISCONNECT_MS) {
				alertFiredForEpisode = disconnectedSince;
				try {
					sentry.captureMessage?.(
						`WS disconnected >${PROLONGED_DISCONNECT_MS / 1000}s cumulative while tab foregrounded: room=${roomShort}`,
						{
							level: 'error',
							extra: { ...baseData, downtimeMs: downMs, closeCode: lastCloseCode, closeReason: lastCloseReason },
							// Indexed tags (unlike `extra`) are filterable/groupable in Sentry
							// Discover aggregations — needed to answer "is this closeCode 1006
							// (abnormal network drop) or 4408 (server/ALB idle timeout)?" across
							// the whole ws-disconnected-cumulative issue without opening every
							// event individually. Always strings: Sentry tags reject non-string
							// values, and an unset code/reason is itself a meaningful bucket.
							tags: {
								closeCode: lastCloseCode !== undefined ? String(lastCloseCode) : 'unknown',
								closeReason: lastCloseReason || 'unknown',
							},
							// Sentry's default fuzzy grouping fragments this message across
							// many small-looking issues (one per stack-trace-less message
							// variant), hiding the true blast radius across the fleet. Group
							// every occurrence into one issue per room-type instead; drill
							// into individual rooms/users via the `room`/`downtimeMs` extras.
							fingerprint: ['ws-disconnected-cumulative', metadata.prefix],
						},
					);
				} catch {
					// Sentry should never throw, but never let telemetry block real work.
				}
				reportCumulativeDowntime(metadata.prefix, downMs, lastCloseCode, lastCloseReason);
			}
		}

		stopTimerIfHealthy();
	}

	const markDisconnectedIfNeeded = () => {
		if (disconnectedSince === null) {
			disconnectedSince = Date.now();
			alertFiredForEpisode = null;
		}
		ensureTimerRunning();
	};

	const markConnected = () => {
		disconnectedSince = null;
		alertFiredForEpisode = null;
		stopTimerIfHealthy();
	};

	const markSynced = () => {
		disconnectedSince = null;
		alertFiredForEpisode = null;
		stopTimerIfHealthy();
	};

	// A timer that fires while the tab is hidden is silently dropped by the
	// `isForegrounded()` guard above — without this, an outage that started
	// (and crossed the threshold) entirely in the background would never
	// alert even after the user returns and starts relying on stale data
	// again. Re-check once the tab becomes visible — but after
	// VISIBLE_RECOVERY_GRACE_MS, not synchronously (see that constant's
	// comment): most hidden-then-visible transitions are wsReconnectCoordinator's
	// own deliberate pause-on-hide reconnecting normally within a couple of
	// seconds, which must not be misreported as a "prolonged disconnect."
	const onVisibilityChange = () => {
		if (!isForegrounded()) return;
		if (visibleRecoveryTimer !== null) clearTimeout(visibleRecoveryTimer);
		visibleRecoveryTimer = setTimeout(() => {
			visibleRecoveryTimer = null;
			checkPeriodic();
		}, VISIBLE_RECOVERY_GRACE_MS);
	};
	if (typeof document !== 'undefined') {
		document.addEventListener('visibilitychange', onVisibilityChange);
	}

	// ── Provider event handlers ──────────────────────────────────────────────

	const onStatus = (event: { status?: string } | string) => {
		const status = typeof event === 'string' ? event : event?.status;
		if (!status) return;
		const level = status === 'disconnected' ? 'warning' : 'info';
		breadcrumb(`status=${status} room=${roomShort}`, level, { status });
		if (status === 'connecting') startFirstUsefulSpan();
		if (status === 'connected') {
			markConnected();
		} else {
			markDisconnectedIfNeeded();
		}
	};

	const onSync = (synced: boolean) => {
		if (synced) {
			if (!firstSyncSeen) {
				firstSyncSeen = true;
				endFirstUsefulSpan('sync');
			}
			markSynced();
		}
		breadcrumb(`sync=${synced} room=${roomShort}`, 'info', { synced });
	};

	const onConnectionClose = (closeEvent: { code?: number; reason?: string } | undefined) => {
		const code = closeEvent?.code;
		const reason = closeEvent?.reason;
		lastCloseCode = code;
		lastCloseReason = reason;
		// 1006 = abnormal close (network blip), 1008 = oversized payload (server kill),
		// 4503 = our user_in_recovery — escalate severity for the meaningful ones.
		const isOverflowKill = code === 1008;
		const isUserInRecovery = code === 4503 || reason === 'user_in_recovery';
		const isAbnormal = code === 1006;
		const level = isOverflowKill || isUserInRecovery ? 'error'
			: isAbnormal ? 'warning'
			: 'info';
		breadcrumb(
			`close code=${code ?? '?'} reason=${reason ?? ''} room=${roomShort}`,
			level,
			{ code, reason },
		);
	};

	const onConnectionError = (errorEvent: { message?: string } | Error | undefined) => {
		const message = errorEvent && 'message' in errorEvent ? errorEvent.message : String(errorEvent);
		breadcrumb(`error: ${message ?? 'unknown'} room=${roomShort}`, 'error', { message });
	};

	provider.on('status', onStatus);
	provider.on('sync', onSync);
	provider.on('connection-close', onConnectionClose);
	provider.on('connection-error', onConnectionError);

	// Initial breadcrumb so we know the provider was created (cheap context
	// for any later error in this session).
	breadcrumb(`attach room=${roomShort}`, 'debug');

	let disposed = false;
	return {
		dispose() {
			if (disposed) return;
			disposed = true;
			try { provider.off('status', onStatus); } catch {}
			try { provider.off('sync', onSync); } catch {}
			try { provider.off('connection-close', onConnectionClose); } catch {}
			try { provider.off('connection-error', onConnectionError); } catch {}
			if (cumulativeCheckTimer !== null) {
				clearInterval(cumulativeCheckTimer);
				cumulativeCheckTimer = null;
			}
			if (visibleRecoveryTimer !== null) {
				clearTimeout(visibleRecoveryTimer);
				visibleRecoveryTimer = null;
			}
			if (typeof document !== 'undefined') {
				document.removeEventListener('visibilitychange', onVisibilityChange);
			}
			if (firstUsefulSpan) endFirstUsefulSpan('aborted', 'disposed');
		},
		abortFirstUsefulMessageSpan(reason: string) {
			if (firstUsefulSpan) endFirstUsefulSpan('aborted', reason);
		},
	};
}
